import { useEffect, useState } from 'react';
import {
  listDestinations,
  createDestination,
  updateDestination,
  deleteDestination,
  uploadAdminImage,
} from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const emptyForm = {
  id: null,
  name: '',
  slug: '',
  summary: '',
  description: '',
  imageUrl: '',
  isActive: true,
  sortOrder: 0,
};

const AdminPackageDestinations = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listDestinations();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      slug: item.slug || '',
      summary: item.summary || '',
      description: item.description || '',
      imageUrl: item.image_url || '',
      isActive: item.is_active !== false,
      sortOrder: item.sort_order || 0,
    });
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        summary: form.summary,
        description: form.description,
        image_url: form.imageUrl,
        is_active: form.isActive,
        sort_order: form.sortOrder,
      };
      if (form.id) await updateDestination(form.id, payload);
      else await createDestination(payload);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this destination?')) return;
    try {
      await deleteDestination(form.id);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, imageUrl: res.path }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Destinations</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(emptyForm)}>
            New destination
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          {loading && <p className="text-sm text-slate-500">Loading...</p>}
          <div className="space-y-2">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => selectItem(item)}
                className={cx(
                  'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm',
                  form.id === item.id ? 'bg-slate-100' : 'hover:bg-slate-50'
                )}
              >
                <span className="font-semibold text-slate-900">{item.name}</span>
                <span className="text-xs text-slate-500">
                  {item.slug} • {item.trip_count || 0} trips
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Destination name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Slug"
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
          />
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={3}
            placeholder="Summary"
            value={form.summary}
            onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
          />
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={4}
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <div className="flex items-center gap-3 text-sm">
            <input type="file" onChange={handleUpload} />
            {form.imageUrl && <span className="text-xs text-slate-500">Image selected</span>}
          </div>
          <input
            type="number"
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Sort order"
            value={form.sortOrder}
            onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value || 0) }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button variant="outline" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={handleSave}>Save destination</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageDestinations;
